// The document archive. ONE archive, reached from the work that produced the
// document — never a folder tree, never a path, never a container name.
//
// Three of Jacky's standing rules are load-bearing here:
//   • search completes as he types (he searches by what he remembers: a job,
//     a supplier, a month — never a filename he never chose);
//   • the newest month is open and the rest are collapsed, expanding in place;
//   • a screen that scrolls owes him a way back to the top, within reach.
//
// Grouping, labels and which sources appear all come from the `document_archive`
// config through the API. Nothing about the shape is hard-coded here, so a
// config edit changes the screen without a deploy.
import {useEffect,useMemo,useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {useSearchParams} from "react-router-dom";
import {Button,Input} from "@aiw/ui";
import {api,client} from "../lib/api";
import {day} from "../lib/format";
import {Page,Panel} from "../shell/Page";
import {JobDocumentWorkspace} from '../components/job-document-workspace';
import {
  backToTopVisible,countItems,filterGroups,groupStartsExpanded,
  matchingSuggestions,suggestions,
  type ArchiveBrowse,type ArchiveGroup,type ArchiveItem,type Suggestion,
} from "../lib/archive";

const EMPTY: ArchiveBrowse={entries:[],groups:[]};

export function Archive(){
  const [params,setParams]=useSearchParams();
  const job=params.get("job")??params.get("job_ref");
  const [typed,setTyped]=useState("");
  const [committed,setCommitted]=useState("");
  const [open,setOpen]=useState<string[]|null>(null);

  const browse=useQuery({
    queryKey:["archive",job],
    queryFn:()=>api.archive(client,job?{job}:{}),
  });

  const data=browse.data??EMPTY;
  const list=useMemo(()=>suggestions(data),[data]);
  const matches=useMemo(()=>matchingSuggestions(list,typed),[list,typed]);
  const groups=useMemo(()=>filterGroups(data.groups,committed),[data.groups,committed]);

  // Which groups are expanded. The newest month opens; everything else follows
  // `groupStartsExpanded`, and a click toggles in place.
  const expanded=useMemo(()=>{
    if(open)return new Set(open);
    return new Set(groups.filter(groupStartsExpanded).map((g)=>groupKey(g)));
  },[groups,open]);

  const toggle=(g:ArchiveGroup)=>{
    const key=groupKey(g);
    const next=new Set(expanded);
    if(next.has(key))next.delete(key);else next.add(key);
    setOpen([...next]);
  };

  const shown=countItems(groups);
  const total=countItems(data.groups);

  return <Page
    kicker="Documents"
    title="Document archive"
    lede="Purchase orders, installation records and every email or text sent, filed by the work they came from. No folders to open."
    actions={job?<Button variant="outline" size="sm" onClick={()=>setParams({})}>Show everything</Button>:undefined}
  >
    {job&&<div className="mb-3 rounded-md border bg-muted/40 px-3 py-2 text-sm">
      Scoped to <span className="font-medium">{job}</span>. <button type="button" className="underline" onClick={()=>setParams({})}>Show the whole archive</button>
    </div>}
    {job&&<JobDocumentWorkspace jobRef={job}/>}

    <div className="relative mb-4">
      <label className="sr-only" htmlFor="archive-search">Search the archive</label>
      <Input
        id="archive-search"
        value={typed}
        autoComplete="off"
        placeholder="Search a job, a supplier or a month — starts matching as you type"
        onChange={(e)=>{setTyped(e.target.value);setCommitted(e.target.value);}}
        onKeyDown={(e)=>{
          if(e.key==="Escape"){setTyped("");setCommitted("");}
          if(e.key==="Enter"&&matches[0]){setTyped(matches[0].value);setCommitted(matches[0].value);}
        }}
      />
      {matches.length>0&&<ul className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border bg-popover shadow-md" role="listbox" aria-label="Search suggestions">
        {matches.map((s)=><li key={`${s.kind}:${s.value}`}>
          <button type="button" role="option" aria-selected={false}
            className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
            onClick={()=>{setTyped(s.value);setCommitted(s.value);}}>
            <span className="truncate">{s.value}</span>
            <span className="shrink-0 text-micro text-muted-foreground">{kindWords(s.kind)}</span>
          </button>
        </li>)}
      </ul>}
    </div>

    {browse.isPending&&<p role="status" className="text-sm text-muted-foreground">Loading the archive…</p>}
    {browse.isError&&<p role="alert" className="text-sm text-destructive">{browse.error instanceof Error?browse.error.message:"The archive could not be loaded. Refresh before acting."}</p>}

    {!browse.isPending&&!browse.isError&&<>
      <p className="mb-3 text-sm text-muted-foreground">
        {committed.trim()?`${shown} of ${total} additional archived file${total===1?"":"s"} match “${committed.trim()}”.`:`${total} additional archived file${total===1?"":"s"}.`}
      </p>

      {groups.length===0?<Panel><p className="text-sm text-muted-foreground">
        {data.groups.length===0?"No archived purchase order files, report photos or sent messages for this selection.":"Nothing matched that. Try a job number, a supplier, or a month."}
      </p></Panel>:<div className="space-y-3">
        {groups.map((g)=><Group key={groupKey(g)} group={g} open={expanded.has(groupKey(g))} onToggle={()=>toggle(g)}/>)}
      </div>}
    </>}

    <BackToTop/>
  </Page>;
}

const groupKey=(g:ArchiveGroup)=>`${g.source}:${g.group_by}:${g.label}`;
const kindWords=(k:Suggestion["kind"])=>k==="job"?"job":k==="supplier"?"supplier":k==="month"?"month":"document";

function Group({group,open,onToggle}:{group:ArchiveGroup;open:boolean;onToggle:()=>void}){
  return <section className="rounded-lg border bg-card">
    <button type="button" onClick={onToggle} aria-expanded={open}
      className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left">
      <span className="min-w-0">
        <span className="block truncate font-display text-sm font-semibold">{group.label}</span>
        <span className="block text-micro text-muted-foreground">{group.items.length} document{group.items.length===1?"":"s"} · grouped by {group.group_by.replace(/_/g," ")}</span>
      </span>
      <span aria-hidden="true" className="shrink-0 text-muted-foreground">{open?"−":"+"}</span>
    </button>
    {open&&<ul className="divide-y border-t">
      {group.items.map((item)=><DocumentRow key={item.id} item={item}/>)}
    </ul>}
  </section>;
}

function DocumentRow({item}:{item:ArchiveItem}){
  const [busy,setBusy]=useState(false);
  const [failed,setFailed]=useState(false);

  async function open(){
    setBusy(true);setFailed(false);
    try{
      const blob=await api.archiveFile(client,item.id);
      const url=URL.createObjectURL(blob);
      window.open(url,"_blank","noopener");
      // The tab holds its own reference; this only releases ours.
      setTimeout(()=>URL.revokeObjectURL(url),60_000);
    }catch{setFailed(true);}finally{setBusy(false);}
  }

  return <li className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
    <div className="min-w-0">
      <p className="truncate text-sm font-medium">{item.title}</p>
      {/* The signed-off shape: the job and who it is for, so a row can be
          scanned without opening it. Either half may legitimately be missing,
          and a missing half is omitted rather than filled with a guess. */}
      {(item.reference||item.customer)&&<p className="truncate text-sm">{[item.reference,item.customer].filter(Boolean).join(" · ")}</p>}
      <p className="text-sm text-muted-foreground">{item.detail}</p>
      {/* The number the supplier already saw on the email, from the one shared
          implementation — it sits with the metadata rather than shouting. */}
      <p className="text-micro text-muted-foreground">{item.order_number?`${item.order_number} · `:""}{item.recorded_at?`Recorded ${day(item.recorded_at)}`:"No date recorded"}</p>
      {failed&&<p role="alert" className="text-sm text-destructive">That document could not be opened. Retry or ask the office to check the archived file.</p>}
    </div>
    {item.available
      ?<Button size="sm" variant="outline" disabled={busy} onClick={()=>void open()}>{busy?"Opening…":"Open"}</Button>
      :<span className="shrink-0 text-micro text-muted-foreground">No archived copy yet</span>}
  </li>;
}

/** A long screen owes him a way back, and it belongs within reach — bottom-right,
 *  where his hand already is, not hunted for in a corner. It only appears once
 *  the page has actually scrolled. */
function BackToTop(){
  const [show,setShow]=useState(false);
  useEffect(()=>{
    const onScroll=()=>setShow(backToTopVisible(window.scrollY));
    onScroll();
    window.addEventListener("scroll",onScroll,{passive:true});
    return()=>window.removeEventListener("scroll",onScroll);
  },[]);
  if(!show)return null;
  return <button type="button" onClick={()=>window.scrollTo({top:0,behavior:"smooth"})}
    className="fixed bottom-6 right-6 z-30 rounded-full border bg-card px-4 py-2 text-sm font-medium shadow-md hover:bg-accent">
    ↑ Back to top
  </button>;
}
